import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
    doc,
    getDoc,
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { db, functions, storage, auth } from "../../firebase";
import Sidebar from "./Sidebar";
import CourseOverview from "./CourseOverview";
import TopicView from "./TopicView";
import LoadingState from "../../components/LoadingState";
import ErrorState from "../../components/ErrorState";
import NotificationPopup from "../../components/NotificationPopup";

type FileItem = {
    name: string;
    path: string;
};

type TopicTab = "files" | "chat" | "tasks" | "kahoot";

type Topic = {
    id: string;
    title: string;
    files?: FileItem[];
};

export default function CourseDashboard() {
    const { courseId } = useParams<{ courseId: string }>();
    const navigate = useNavigate();

    const [course, setCourse] = useState<any>(null);
    const [topics, setTopics] = useState<Topic[]>([]);
    const [activeTopicId, setActiveTopicId] = useState<string | null>(null);
    const [newTopicTitle, setNewTopicTitle] = useState("");
    const [loadingGoals, setLoadingGoals] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState<TopicTab>("files");
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [sidebarWidth, setSidebarWidth] = useState<number>(280);
    const [isResizing, setIsResizing] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [popupType, setPopupType] = useState<'info' | 'success' | 'warning' | 'error'>('info');
    const [editingGoals, setEditingGoals] = useState<string[]>([]);
    const [isEditingGoals, setIsEditingGoals] = useState(false);
    const [description, setDescription] = useState("");
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

    const sidebarRef = useRef<HTMLDivElement>(null);
    const activeTopic = topics.find((t) => t.id === activeTopicId) || null;

    // Fenstergröße-Listener für responsive Design
    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 1024);
            if (window.innerWidth >= 1024 && !isMobileSidebarOpen) {
                setIsMobileSidebarOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMobileSidebarOpen]);

    // Popup-Funktion
    const showNotification = (message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
        setPopupMessage(message);
        setPopupType(type);
        setShowPopup(true);
        setTimeout(() => {
            setShowPopup(false);
        }, 3000);
    };

    // Resizable Sidebar Logic - nur auf Desktop
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing || !sidebarRef.current || !isDesktop) return;
            e.preventDefault();
            const newWidth = Math.min(Math.max(e.clientX, 200), 500);
            setSidebarWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            document.body.style.cursor = 'auto';
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, isDesktop]);

    // Load saved sidebar width from localStorage on mount
    useEffect(() => {
        const savedWidth = localStorage.getItem('sidebarWidth');
        if (savedWidth) {
            setSidebarWidth(parseInt(savedWidth, 10));
        }
    }, []);

    // Save sidebar width to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('sidebarWidth', sidebarWidth.toString());
    }, [sidebarWidth]);

    /* ---------------- LOAD DATA ---------------- */
    useEffect(() => {
        if (!courseId) return;

        const load = async () => {
            setLoading(true);
            try {
                // Load course data
                const courseSnap = await getDoc(doc(db, "courses", courseId));
                if (courseSnap.exists()) {
                    const c: any = { id: courseSnap.id, ...courseSnap.data() };
                    setCourse(c);
                    setEditingGoals(c.learningGoals || []);
                    setDescription(c.description || "");
                }

                // Load topics
                const topicSnap = await getDocs(
                    collection(db, "courses", courseId, "topics")
                );

                const loadedTopics = topicSnap.docs.map((d) => ({
                    id: d.id,
                    ...d.data(),
                })) as Topic[];

                setTopics(loadedTopics);

            } catch (error) {
                console.error("Error loading course:", error);
                showNotification("Fehler beim Laden des Kurses", "error");
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [courseId]);

    // Reset tab when topic changes
    useEffect(() => {
        setActiveTab("files");
    }, [activeTopicId]);

    /* ---------------- COURSE DELETE ---------------- */
    const deleteCourse = async () => {
        if (!courseId || !course) return;

        const ok = window.confirm(
            `Willst du den Kurs "${course.title}" wirklich löschen?\n\nAlle Themen und Materialien gehen verloren.`
        );
        if (!ok) return;

        setDeleting(true);
        try {
            await deleteDoc(doc(db, "courses", courseId));
            showNotification("Kurs erfolgreich gelöscht", "success");
            setTimeout(() => {
                navigate("/courses");
            }, 1500);
        } catch (error) {
            showNotification("Fehler beim Löschen des Kurses", "error");
        } finally {
            setDeleting(false);
        }
    };

    /* ---------------- TOPICS ---------------- */
    const createTopic = async () => {
        if (!newTopicTitle.trim() || !courseId) return;

        const topicToCreate = newTopicTitle.trim();
        setNewTopicTitle("");

        const tempId = `temp-${Date.now()}`;
        const tempTopic: Topic = {
            id: tempId,
            title: topicToCreate,
            files: []
        };

        setTopics((prev) => [...prev, tempTopic]);

        try {
            const refDoc = await addDoc(
                collection(db, "courses", courseId, "topics"),
                {
                    title: topicToCreate,
                    createdAt: new Date(),
                    files: [],
                }
            );

            setTopics((prev) =>
                prev.map((t) =>
                    t.id === tempId ? { ...tempTopic, id: refDoc.id } : t
                )
            );

            showNotification("Thema erfolgreich erstellt", "success");
        } catch (error) {
            setTopics((prev) => prev.filter((t) => t.id !== tempId));
            showNotification("Fehler beim Erstellen des Themas", "error");
        }
    };

    const deleteTopic = async (topic: Topic) => {
        if (!courseId) return;

        const ok = window.confirm(
            `Thema "${topic.title}" wirklich löschen?\n\nAlle Dateien dieses Themas gehen verloren.`
        );
        if (!ok) return;

        setTopics((prev) => prev.filter((t) => t.id !== topic.id));

        if (activeTopicId === topic.id) {
            setActiveTopicId(null);
        }

        try {
            await deleteDoc(doc(db, "courses", courseId, "topics", topic.id));
            showNotification("Thema erfolgreich gelöscht", "success");
        } catch (error) {
            showNotification("Fehler beim Löschen des Themas", "error");
        }
    };

    /* ---------------- FILE UPLOAD ---------------- */
    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !activeTopic || !courseId) return;
        if (!auth.currentUser) {
            showNotification("Bitte melde dich an, um Dateien hochzuladen", "warning");
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        const uploaded: FileItem[] = [];
        const files = Array.from(e.target.files);
        const totalFiles = files.length;

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const storageRef = ref(
                    storage,
                    `courses/${courseId}/topics/${activeTopic.id}/${Date.now()}_${file.name}`
                );

                await uploadBytes(storageRef, file);
                uploaded.push({ name: file.name, path: storageRef.fullPath });

                setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
            }

            const newFiles = [...(activeTopic.files || []), ...uploaded];

            await updateDoc(
                doc(db, "courses", courseId, "topics", activeTopic.id),
                { files: newFiles }
            );

            setTopics((prev) =>
                prev.map((t) =>
                    t.id === activeTopic.id ? { ...t, files: newFiles } : t
                )
            );

            showNotification(`${uploaded.length} Dateien erfolgreich hochgeladen`, "success");
        } catch (error) {
            showNotification("Fehler beim Hochladen der Dateien", "error");
        } finally {
            setUploading(false);
            setUploadProgress(0);
            e.target.value = "";
        }
    };

    /* ---------------- FILE DOWNLOAD ---------------- */
    const downloadFile = async (file: FileItem) => {
        if (!auth.currentUser) {
            showNotification("Bitte melde dich an, um Dateien herunterzuladen", "warning");
            return;
        }

        try {
            const storageRef = ref(storage, file.path);
            const url = await getDownloadURL(storageRef);

            const a = document.createElement("a");
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            a.remove();

            showNotification("Datei wird heruntergeladen", "success");
        } catch (error) {
            showNotification("Datei konnte nicht heruntergeladen werden", "error");
        }
    };

    /* ---------------- COURSE DESCRIPTION ---------------- */
    const saveDescription = async () => {
        if (!courseId) return;

        try {
            await updateDoc(doc(db, "courses", courseId), { description });
            setCourse((prev: any) => ({ ...prev, description }));
            showNotification("Beschreibung gespeichert", "success");
        } catch (error) {
            showNotification("Beschreibung konnte nicht gespeichert werden", "error");
        }
    };

    /* ---------------- LEARNING GOALS ---------------- */
    const generateLearningGoals = async () => {
        if (!course || !courseId) return;

        setLoadingGoals(true);

        try {
            const call = httpsCallable(functions, "generateLearningGoals");
            const payload: any = { topic: course.title };
            if (course.grade) payload.grade = course.grade;

            const res: any = await call(payload);

            if (res.data?.goals) {
                await updateDoc(doc(db, "courses", courseId), {
                    learningGoals: res.data.goals,
                });
                setCourse({ ...course, learningGoals: res.data.goals });
                setEditingGoals(res.data.goals);
                showNotification("Lernziele erfolgreich generiert", "success");
            }
        } catch (error) {
            showNotification("Lernziele konnten nicht generiert werden", "error");
        }

        setLoadingGoals(false);
    };

    const startEditGoals = () => {
        setEditingGoals(course.learningGoals || []);
        setIsEditingGoals(true);
    };

    const cancelEditGoals = () => {
        setEditingGoals(course.learningGoals || []);
        setIsEditingGoals(false);
    };

    const saveGoals = async () => {
        if (!courseId) return;

        try {
            await updateDoc(doc(db, "courses", courseId), {
                learningGoals: editingGoals,
            });

            setCourse((prev: any) => ({
                ...prev,
                learningGoals: editingGoals,
            }));

            setIsEditingGoals(false);
            showNotification("Lernziele gespeichert", "success");
        } catch (error) {
            showNotification("Lernziele konnten nicht gespeichert werden", "error");
        }
    };

    const handleGoalChange = (index: number, value: string) => {
        setEditingGoals((prev) =>
            prev.map((g, i) => (i === index ? value : g))
        );
    };

    const addGoal = () => setEditingGoals((prev) => [...prev, ""]);

    const removeGoal = (index: number) =>
        setEditingGoals((prev) => prev.filter((_, i) => i !== index));

    /* ---------------- LOADING STATE ---------------- */
    if (loading) {
        return <LoadingState
            message="Lade Kursdaten..."
            fullScreen={true}
            spinnerSize="large"
        />;
    }

    if (!course) {
        return <ErrorState
            title="Kurs nicht gefunden"
            message="Der angeforderte Kurs existiert nicht oder du hast keine Berechtigung."
            buttonText="← Zurück zu den Kursen"
            onButtonClick={() => navigate("/courses")}
            icon="❌"
            showButton={true}
        />;
    }

    return (
        <div className="flex flex-col lg:flex-row min-h-[calc(100vh-64px)] bg-gradient-to-br from-gray-50 to-white text-gray-800 font-sans relative overflow-x-hidden">
            {/* Animated Background - light version */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(76,175,80,0.08)_0%,transparent_50%)]"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(33,150,243,0.05)_0%,transparent_50%)] animate-gradient-shift"></div>

            {/* Notification Popup */}
            <NotificationPopup
                showPopup={showPopup}
                popupMessage={popupMessage}
                type={popupType}
                position="bottom-right"
            />

            {/* Mobile Sidebar Toggle Button */}
            <button
                onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
                className="lg:hidden fixed top-20 left-4 z-40 p-2.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-100 transition-all shadow-lg"
            >
                {isMobileSidebarOpen ? '✕' : '☰'}
            </button>

            {/* Sidebar - Responsive */}
            <div
                ref={sidebarRef}
                className={`lg:fixed lg:left-0 bg-white/95 backdrop-blur-xl border-r border-gray-200 z-30 flex flex-col transition-all duration-300 min-w-[200px] max-w-[500px] h-screen
                    ${isMobileSidebarOpen
                    ? 'fixed left-0 top-0 w-80 translate-x-0 shadow-2xl'
                    : 'fixed -translate-x-full lg:translate-x-0'
                }`}
                style={{
                    width: isDesktop ? `${sidebarWidth}px` : '280px',
                    height: 'calc(100vh - 64px)',
                    top: '64px',
                }}
            >
                <Sidebar
                    sidebarWidth={sidebarWidth}
                    isResizing={isResizing}
                    setIsResizing={setIsResizing}
                    course={course}
                    topics={topics}
                    activeTopicId={activeTopicId}
                    setActiveTopicId={(id) => {
                        setActiveTopicId(id);
                        if (!isDesktop) setIsMobileSidebarOpen(false);
                    }}
                    newTopicTitle={newTopicTitle}
                    setNewTopicTitle={setNewTopicTitle}
                    createTopic={createTopic}
                    navigate={navigate}
                    isDesktop={isDesktop}
                    onCloseMobile={() => setIsMobileSidebarOpen(false)}
                />
            </div>

            {/* Mobile Overlay für Sidebar */}
            {isMobileSidebarOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-20"
                    onClick={() => setIsMobileSidebarOpen(false)}
                />
            )}

            {/* Main Content - Responsive */}
            <main
                className="flex-1 p-4 md:p-6 lg:p-8 relative z-10 transition-all duration-200"
                style={{
                    marginLeft: isDesktop ? `${sidebarWidth}px` : '0'
                }}
            >
                {/* Mobile Info Banner */}
                {!isMobileSidebarOpen && (
                    <div className="lg:hidden mb-4 p-3 bg-gray-100/80 border border-gray-200 rounded-lg text-sm text-gray-600">
                        👈 Tippe auf das Menüsymbol, um die Themen zu sehen
                    </div>
                )}

                {!activeTopicId ? (
                    <CourseOverview
                        course={course}
                        topics={topics}
                        deleteCourse={deleteCourse}
                        deleting={deleting}
                        description={description}
                        setDescription={setDescription}
                        saveDescription={saveDescription}
                        learningGoals={course.learningGoals}
                        editingGoals={editingGoals}
                        isEditingGoals={isEditingGoals}
                        loadingGoals={loadingGoals}
                        generateLearningGoals={generateLearningGoals}
                        startEditGoals={startEditGoals}
                        cancelEditGoals={cancelEditGoals}
                        saveGoals={saveGoals}
                        handleGoalChange={handleGoalChange}
                        addGoal={addGoal}
                        removeGoal={removeGoal}
                        showNotAvailablePopup={(feature) => showNotification(`${feature} ist in Kürze verfügbar!`, 'info')}
                        setActiveTopicId={(id) => {
                            setActiveTopicId(id);
                            if (!isDesktop) setIsMobileSidebarOpen(false);
                        }}
                    />
                ) : (
                    <TopicView
                        activeTopic={activeTopic}
                        setActiveTopicId={(id) => {
                            setActiveTopicId(id);
                            if (!isDesktop) setIsMobileSidebarOpen(false);
                        }}
                        deleteTopic={deleteTopic}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        handleFileUpload={handleFileUpload}
                        uploading={uploading}
                        uploadProgress={uploadProgress}
                        downloadFile={downloadFile}
                        course={course}
                    />
                )}
            </main>
        </div>
    );
}